import React, { useState } from "react";
import { AlertTriangle, TrendingUp, TrendingDown, Info, ChevronDown, ChevronUp } from "lucide-react";

const SEVERITY = {
  high:   { color: "#FF1744", bg: "rgba(255, 23, 68, 0.08)",  label: "HIGH",   Icon: AlertTriangle },
  medium: { color: "#FFB800", bg: "rgba(255, 184, 0, 0.08)",  label: "REVIEW", Icon: Info },
  info:   { color: "#00E676", bg: "rgba(0, 230, 118, 0.06)",  label: "IDEA",   Icon: Info },
};

const ACTION = {
  add:    { Icon: TrendingUp,   color: "var(--gain)", verb: "Add" },
  reduce: { Icon: TrendingDown, color: "var(--loss)", verb: "Reduce" },
  alert:  { Icon: Info,         color: "var(--text-secondary)", verb: "Note" },
};

export default function RebalanceSuggestions({ suggestions, onQuickAdd, onScrollToTicker }) {
  const [expanded, setExpanded] = useState(true);
  const list = suggestions || [];

  const counts = {
    high: list.filter((s) => s.severity === "high").length,
    medium: list.filter((s) => s.severity === "medium").length,
    info: list.filter((s) => s.severity === "info").length,
  };

  return (
    <section data-testid="rebalance-section">
      <div className="flex items-baseline justify-between mb-4">
        <div>
          <p className="label-xs mb-1">Actionable</p>
          <h2 className="font-serif text-2xl">Rebalance suggestions</h2>
        </div>
        <div className="flex items-center gap-3 text-[10px]" style={{ color: "var(--text-secondary)" }}>
          {counts.high > 0 && <span style={{ color: SEVERITY.high.color }}>● {counts.high} high</span>}
          {counts.medium > 0 && <span style={{ color: SEVERITY.medium.color }}>● {counts.medium} review</span>}
          {counts.info > 0 && <span style={{ color: SEVERITY.info.color }}>● {counts.info} ideas</span>}
          <button
            className="btn-ghost inline-flex items-center gap-1 ml-2"
            onClick={() => setExpanded((e) => !e)}
            data-testid="toggle-suggestions"
          >
            {expanded ? <ChevronUp size={12} /> : <ChevronDown size={12} />}
            {expanded ? "collapse" : "expand"}
          </button>
        </div>
      </div>

      {!expanded ? null : list.length === 0 ? (
        <div className="card text-xs py-8 text-center" style={{ color: "var(--text-secondary)" }}>
          Your portfolio looks well-balanced. No urgent actions.
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {list.map((s, i) => {
            const sev = SEVERITY[s.severity] || SEVERITY.info;
            const act = ACTION[s.action] || ACTION.alert;
            return (
              <div
                key={i}
                data-testid={`suggestion-${s.ticker}-${i}`}
                className="card p-4 flex gap-3"
                style={{ borderLeft: `3px solid ${sev.color}`, background: sev.bg }}
              >
                <div className="flex-shrink-0 mt-1">
                  <act.Icon size={16} style={{ color: act.color }} />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <span
                      className="text-[9px] px-1.5 py-0.5 tracking-widest"
                      style={{ color: sev.color, border: `1px solid ${sev.color}` }}
                    >
                      {sev.label}
                    </span>
                    <span className="font-mono text-xs font-semibold">{s.ticker}</span>
                  </div>
                  <p className="text-sm font-serif mb-1">{s.title}</p>
                  <p className="text-[11px] leading-relaxed" style={{ color: "var(--text-secondary)" }}>
                    {s.message}
                  </p>
                  {s.action === "add" && onQuickAdd && (
                    <button
                      data-testid={`suggestion-add-${s.ticker}-${i}`}
                      className="btn-secondary !py-1 !px-3 !text-[10px] mt-3"
                      onClick={() => onQuickAdd({ ticker: s.ticker, cost: "" })}
                    >
                      + Add {s.ticker}
                    </button>
                  )}
                  {s.action === "reduce" && onScrollToTicker && (
                    <button
                      data-testid={`suggestion-view-${s.ticker}-${i}`}
                      className="btn-secondary !py-1 !px-3 !text-[10px] mt-3"
                      onClick={() => onScrollToTicker(s.ticker)}
                    >
                      View position
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      <p className="text-[10px] mt-3" style={{ color: "var(--text-secondary)" }}>
        Suggestions derived from analyst consensus, position concentration, and dividend outlook. Not financial advice.
      </p>
    </section>
  );
}
